/**
 * 가드레일 창들을 앱에 붙이는 곳 — 관문(utils/guardrailGate)의 처리기를 등록하고, 기본 axios 에 가로채기를 건다.
 *   · 확인 전(428)        → GuardrailModal(ack) → 확인하면 같은 요청을 다시
 *   · 결정 필요(409)      → 결정 창 → 저장하면 같은 요청을 다시
 *   · 내보내기 검사        → 내보내기 창(빼기·치환·그대로)
 *   · 메뉴 「AI 가드레일」 → window 이벤트 `livesql:open-guardrail`
 * docs/features/21-ai-guardrail.md
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiShield } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import GuardrailModal from './GuardrailModal';
import { setGuardrailHandler, setExportHandler, installGuardrailInterceptor } from '../../utils/guardrailGate';
import { formatApiError } from '../../utils/formatters';
import * as api from '../../services/guardrail';
import './Guardrail.css';

export const OPEN_GUARDRAIL_EVENT = 'livesql:open-guardrail';
const DECISIONS = ['mask', 'exclude', 'send'];

export function openGuardrail() {
  window.dispatchEvent(new CustomEvent(OPEN_GUARDRAIL_EVENT));
}

function DecisionDialog({ state, onDone }) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState(false);
  if (!state) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.saveDecisions(state.scope, state.pending.map(p => ({ key: p.key, decision: choice[p.key] || 'mask' })));
      onDone(true);
    } catch (e) { toast.error(formatApiError(e, t)); onDone(false); } finally { setBusy(false); }
  };
  return (
    <AppModal overlayClassName="grd-overlay" open onClose={() => onDone(false)} title={t('common:grd.decideTitle')} icon={<FiShield size={14} />} className="app-modal-default grd-small">
      <div className="grd-body">
        <p>{t('common:grd.decideBody')}</p>
        <ul className="grd-decide">
          {state.pending.map(p => (
            <li key={p.key}>
              <div><b>{p.ruleName}</b> <span className="mono">{p.preview}</span></div>
              <div className="grd-radios" role="radiogroup">
                {DECISIONS.map(d => (
                  <label key={d}>
                    <input type="radio" name={p.key} checked={(choice[p.key] || 'mask') === d} onChange={() => setChoice(c => ({ ...c, [p.key]: d }))} />
                    {t(`common:grd.decide.${d}`)}
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ul>
        <p className="grd-note">{t('common:grd.decideScope')}</p>
        <div className="grd-foot">
          <button type="button" className="app-btn" onClick={() => onDone(false)}>{t('common:ui.cancel')}</button>
          <button type="button" className="app-btn app-btn-primary" onClick={submit} disabled={busy}>{t('common:grd.decideGo')}</button>
        </div>
      </div>
    </AppModal>
  );
}

/**
 * 보내기 전 검사(2026-10-20 사용자 결정) — AI 에게 보낼 글을 먼저 만들고 검사한 결과. 걸린 값마다 규칙 이름·어디에 있는지·미리보기와
 * 「변조·빼기·그대로」 를 고른다. 규칙이 정해 둔 동작이 기본으로 골라져 있고, 「물어보기」 규칙만 기본 없이 고르게 한다.
 * 결정은 **이 요청 한 번만**(다음에 보낼 때 다시 묻는다). 「가드레일 보기」 로 정책 창을 함께 볼 수 있다.
 */
function ReviewDialog({ state, onDone, onOpenGuide }) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState(() => Object.fromEntries((state ? state.items : []).filter(i => i.action).map(i => [i.key, i.action])));
  const [busy, setBusy] = useState(false);
  if (!state) return null;
  const missing = state.items.some(i => !choice[i.key]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.saveReview(state.scope, state.items.map(i => ({ key: i.key, decision: choice[i.key] })));
      onDone(true);
    } catch (e) { toast.error(formatApiError(e, t)); onDone(false); } finally { setBusy(false); }
  };
  return (
    <AppModal overlayClassName="grd-overlay" open onClose={() => onDone(false)} title={t('common:grd.reviewTitle')} icon={<FiShield size={14} />} className="app-modal-default grd-small">
      <div className="grd-body">
        {state.firstUse && <p className="grd-first">{t('common:grd.reviewFirstUse')}</p>}
        <p>{t('common:grd.reviewBody', { n: state.items.length })}</p>
        <ul className="grd-decide">
          {state.items.map(i => (
            <li key={i.key}>
              <div>
                <b>{i.ruleName}</b> <span className="mono">{i.preview}</span>
                {i.where && i.where.length ? <span className="grd-where"> · {i.where.map(w => t(`common:grd.where.${w}`, { defaultValue: w })).join(', ')}</span> : null}
                {!i.action ? <span className="grd-ask"> · {t('common:grd.reviewChoose')}</span> : null}
              </div>
              <div className="grd-radios" role="radiogroup">
                {DECISIONS.map(d => (
                  <label key={d}>
                    <input type="radio" name={i.key} checked={choice[i.key] === d} onChange={() => setChoice(c => ({ ...c, [i.key]: d }))} />
                    {t(`common:grd.decide.${d}`)}
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ul>
        <p className="grd-note">{t('common:grd.reviewOnce')}</p>
        <div className="grd-foot">
          <button type="button" className="app-btn" onClick={onOpenGuide}>{t('common:grd.reviewGuide')}</button>
          <span style={{ flex: 1 }} />
          <button type="button" className="app-btn" onClick={() => onDone(false)}>{t('common:ui.cancel')}</button>
          <button type="button" className="app-btn app-btn-primary" onClick={submit} disabled={busy || missing} title={missing ? t('common:grd.reviewChoose') : undefined}>{t('common:grd.reviewGo')}</button>
        </div>
      </div>
    </AppModal>
  );
}

function ExportDialog({ scan, onDone }) {
  const { t } = useTranslation();
  if (!scan) return null;
  return (
    <AppModal overlayClassName="grd-overlay" open onClose={() => onDone(null)} title={t('common:grd.exportTitle')} icon={<FiShield size={14} />} className="app-modal-default grd-small">
      <div className="grd-body">
        <p>{t('common:grd.exportBody', { count: scan.total })}</p>
        <ul className="grd-hits">
          {scan.rules.map(h => (
            <li key={h.ruleId}><b>{h.ruleName}</b> · {t('common:grd.hitCount', { count: h.count })} <span className="mono grd-previews">{h.previews.join(', ')}</span></li>
          ))}
        </ul>
        <div className="grd-foot">
          <button type="button" className="app-btn" onClick={() => onDone(null)}>{t('common:ui.cancel')}</button>
          <button type="button" className="app-btn" onClick={() => onDone('asis')}>{t('common:grd.exportAsIs')}</button>
          <button type="button" className="app-btn" onClick={() => onDone('exclude')}>{t('common:grd.exportExclude')}</button>
          <button type="button" className="app-btn app-btn-primary" onClick={() => onDone('mask')}>{t('common:grd.exportMask')}</button>
        </div>
      </div>
    </AppModal>
  );
}

export default function GuardrailHost({ connections }) {
  const { t } = useTranslation();
  const [modal, setModal] = useState(null);            // null | 'view' | 'ack'
  const [decision, setDecision] = useState(null);      // { scope, pending }
  const [review, setReview] = useState(null);          // { scope, items, firstUse } — 보내기 전 검사
  const [exportScan, setExportScan] = useState(null);
  const waiter = useRef(null);                         // 지금 창이 끝나기를 기다리는 resolve

  const settle = (v) => { const w = waiter.current; waiter.current = null; if (w) w(v); };

  const handle = useCallback(async (errorType) => {
    if (errorType === 'GUARDRAIL_ACK_REQUIRED') {
      return new Promise((resolve) => { waiter.current = resolve; setModal('ack'); });
    }
    let p;
    try { p = await api.getPending(); } catch (_e) { return false; }
    if (p && p.review && p.scope && Array.isArray(p.items) && p.items.length) {
      return new Promise((resolve) => { waiter.current = resolve; setReview(p); });
    }
    if (!p || !p.scope || !p.pending || !p.pending.length) return false;
    return new Promise((resolve) => { waiter.current = resolve; setDecision(p); });
  }, []);

  useEffect(() => setGuardrailHandler(handle), [handle]);
  useEffect(() => setExportHandler((scan) => new Promise((resolve) => { waiter.current = resolve; setExportScan(scan); })), []);
  useEffect(() => installGuardrailInterceptor(axios), []);
  useEffect(() => {
    const open = () => setModal(m => m || 'view');
    window.addEventListener(OPEN_GUARDRAIL_EVENT, open);
    return () => window.removeEventListener(OPEN_GUARDRAIL_EVENT, open);
  }, []);

  return (
    <>
      {/* 확인 창 위에 가드레일 창이 뜨도록 확인 창을 먼저 그린다 */}
      <ReviewDialog key={review ? `${review.scope}:${review.items.map(i => i.key).join(',')}` : 'review-none'} state={review}
        onOpenGuide={() => setModal(m => m || 'view')}
        onDone={(ok) => { setReview(null); settle(ok); }} />
      <GuardrailModal
        open={!!modal}
        mode={modal || 'view'}
        connections={connections}
        // 보내기 전 검사 창에서 「가드레일 보기」 로 연 것이면 닫아도 그 확인은 그대로 기다린다
        onClose={() => { setModal(null); if (!review) settle(false); }}
        onAcknowledged={() => { setModal(null); toast.success(t('common:grd.acked')); settle(true); }}
      />
      <DecisionDialog key={decision ? `${decision.scope}:${decision.pending.length}` : 'none'} state={decision} onDone={(ok) => { setDecision(null); settle(ok); }} />
      <ExportDialog scan={exportScan} onDone={(m) => { setExportScan(null); settle(m); }} />
    </>
  );
}

GuardrailHost.propTypes = {
  /** 「연결에서 이름 가져오기」 후보 연결(App 의 연결 목록) */
  connections: PropTypes.arrayOf(PropTypes.object),
};
